// src/pages/TextbookCreate.jsx
import React, { useState } from "react";
import {
  FiBookOpen,
  FiSave,
  FiPlus,
  FiTrash2,
  FiUpload,
  FiList,
} from "react-icons/fi";
import { useCreateTextbookMutation } from "../context/textbookApi";

const GRADES = Array.from({ length: 11 }, (_, i) => `${i + 1}-sinf`);

const SUBJECTS = [
  "Matematika",
  "Algebra",
  "Geometriya",
  "Fizika",
  "Kimyo",
  "Biologiya",
  "Chizmachilik",
  "Tarix",
  "Ona tili",
  "Adabiyot 1-qism",
  "Adabiyot 2-qism",
];

const LANGS = [
  "O‘zbek tili",
  "Rus tili",
  "Qoraqalpoq tili",
  "Tojik tili",
  "Qirg‘iz tili",
  "Qozoq tili",
  "Turkman tili",
];

const TYPES = ["Asosiy darslik", "Metodik qo‘llanma", "Ish daftari"];

let topicIdSeed = 1;

export default function TextbookCreate() {
  const [createTextbook, { isLoading, isSuccess, isError, error }] =
    useCreateTextbookMutation();

  // PDF fayl
  const [pdfFile, setPdfFile] = useState(null);

  // Asosiy parametrlar
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [lang, setLang] = useState("");
  const [year, setYear] = useState("2019");
  const [kind, setKind] = useState(TYPES[0]);

  // Qo‘shimcha info (hozircha backend majburiy emas)
  const [title, setTitle] = useState("");
  const [authors, setAuthors] = useState("");
  const [publisher, setPublisher] = useState("");
  const [isbn, setIsbn] = useState("");
  const [pages, setPages] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [description, setDescription] = useState("");

  // Mavzular
  const [topics, setTopics] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");

  const [newTopic, setNewTopic] = useState({
    section: "",
    title: "",
    from: "",
    to: "",
    note: "",
  });

  const addTopic = () => {
    if (!newTopic.title.trim()) return;

    setTopics((prev) => [
      ...prev,
      {
        id: topicIdSeed++,
        order: prev.length + 1,
        section: newTopic.section.trim() || "",
        title: newTopic.title.trim(),
        from: newTopic.from.trim() || "",
        to: newTopic.to.trim() || "",
        note: newTopic.note.trim() || "",
      },
    ]);

    setNewTopic({ section: "", title: "", from: "", to: "", note: "" });
  };

  const deleteTopic = (id) => {
    setTopics((prev) =>
      prev
        .filter((t) => t.id !== id)
        .map((t, i) => ({ ...t, order: i + 1 }))
    );
  };

  const handleBulkAdd = () => {
    if (!bulkText.trim()) return;

    const lines = bulkText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

    if (!lines.length) return;

    setTopics((prev) => {
      let startIndex = prev.length;
      const extra = lines.map((line, idx) => ({
        id: topicIdSeed++,
        order: startIndex + idx + 1,
        section: "",
        title: line,
        from: "",
        to: "",
        note: "",
      }));
      return [...prev, ...extra];
    });

    setBulkText("");
    setBulkOpen(false);
  };

  const resetForm = () => {
    setPdfFile(null);
    setGrade("");
    setSubject("");
    setLang("");
    setYear("2019");
    setKind(TYPES[0]);
    setTitle("");
    setAuthors("");
    setPublisher("");
    setIsbn("");
    setPages("");
    setPdfUrl("");
    setDescription("");
    setTopics([]);
    setNewTopic({ section: "", title: "", from: "", to: "", note: "" });
    setBulkText("");
    setBulkOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!grade || !subject || !lang) {
      alert("Sinf, fan va tilni tanlang.");
      return;
    }

    if (!pdfFile && !pdfUrl.trim()) {
      alert("PDF fayl yuklang yoki PDF URL kiriting.");
      return;
    }

    try {
      const formData = new FormData();

      // Backend kutadigan nomlar
      formData.append("grade", grade);
      formData.append("subject", subject);
      formData.append("language", lang);
      formData.append("publishYear", year);
      formData.append("textbookType", kind);

      // Qo‘shimcha maydonlar (hozircha optional)
      formData.append("title", title);
      formData.append("authors", authors);
      formData.append("publisher", publisher);
      formData.append("isbn", isbn);
      formData.append("pages", pages);
      formData.append("pdfUrl", pdfUrl);
      formData.append("description", description);

      // Mavzular massivini backend strukturasi bilan jo‘natamiz
      formData.append(
        "topics",
        JSON.stringify(
          topics.map(({ section, title, from, to, note }) => ({
            section,
            title,
            pageFrom: from || null,
            pageTo: to || null,
            description: note || "",
          }))
        )
      );

      if (pdfFile) {
        // backenddagi multer.single("pdf") bilan mos
        formData.append("pdf", pdfFile);
      }

      await createTextbook(formData).unwrap();

      alert("Darslik muvaffaqiyatli saqlandi!");
      resetForm();
    } catch (err) {
      console.error("Darslik saqlashda xatolik:", err);
      alert("Darslikni saqlashda xatolik yuz berdi. Konsolni tekshiring.");
    }
  };

  const topicCount = topics.length;

  const headerSummary =
    grade && subject && lang
      ? `${grade} • ${subject} • ${lang} • ${year}`
      : "Sinf, fan va tilni tanlang";

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Title */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-2xl bg-blue-100 flex items-center justify-center">
            <FiBookOpen className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Darslik qo‘shish
            </h1>
            <p className="text-sm text-slate-500">
              Sinf, fan, til va mavzular bo‘yicha darslik strukturasi yarating.
            </p>
          </div>
        </div>

        {/* Status */}
        {(isSuccess || isError) && (
          <div className="mb-4">
            {isSuccess && (
              <div className="text-xs px-3 py-2 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-100">
                Darslik muvaffaqiyatli saqlandi.
              </div>
            )}
            {isError && (
              <div className="text-xs px-3 py-2 rounded-xl bg-red-50 text-red-700 border border-red-100">
                Xatolik yuz berdi.{" "}
                <span className="opacity-70 text-[11px]">
                  {error?.data?.message || error?.error || "Nomalum xato"}
                </span>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Block 1: Asosiy parametrlar */}
          <section className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6">
            <div className="flex items-center justify-between gap-4 mb-4">
              <h2 className="text-lg font-semibold text-slate-900">
                1. Asosiy parametrlar
              </h2>
              <span className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-600">
                {headerSummary}
              </span>
            </div>

            <div className="grid md:grid-cols-5 gap-4">
              {/* Sinf */}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-700">
                  Sinf
                </label>
                <select
                  value={grade}
                  onChange={(e) => setGrade(e.target.value)}
                  className="border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50"
                >
                  <option value="">Tanlang</option>
                  {GRADES.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
              </div>

              {/* Fan */}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-700">
                  Fan
                </label>
                <select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50"
                >
                  <option value="">Tanlang</option>
                  {SUBJECTS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              {/* Til */}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-700">
                  Til
                </label>
                <select
                  value={lang}
                  onChange={(e) => setLang(e.target.value)}
                  className="border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50"
                >
                  <option value="">Tanlang</option>
                  {LANGS.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>

              {/* Nashr yili */}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-700">
                  Nashr yili
                </label>
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50"
                  placeholder="2019"
                />
              </div>

              {/* Darslik turi */}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-700">
                  Darslik turi
                </label>
                <select
                  value={kind}
                  onChange={(e) => setKind(e.target.value)}
                  className="border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50"
                >
                  {TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {/* Block 2: Darslik ma'lumotlari + PDF */}
          <section className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 space-y-4">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              2. Darslik ma’lumotlari
            </h2>

            {/* Faqat PDF Upload – dizaynga mos */}
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                PDF fayl yuklash
                <span className="text-[11px] text-slate-500 font-normal">
                  (Eduportal emas, bevosita PDF yuklanadi)
                </span>
              </label>

              <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-3">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) setPdfFile(file);
                  }}
                  className="text-sm text-slate-700"
                />

                {pdfFile && (
                  <span className="text-xs text-green-600 font-medium">
                    {pdfFile.name} yuklandi ✓
                  </span>
                )}
              </div>
            </div>
          </section>

          {/* Block 3: Mavzular */}
          <section className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-slate-900">
                  3. Mavzular strukturasini tuzish
                </h2>
                <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 flex items-center gap-1">
                  <FiList className="w-3 h-3" />
                  Jami: {topicCount} ta mavzu
                </span>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setBulkOpen((x) => !x)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-xs md:text-sm rounded-xl border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                >
                  <FiUpload className="w-4 h-4" />
                  Bulk qo‘shish (matn)
                </button>
              </div>
            </div>

            {/* Bulk textarea */}
            {bulkOpen && (
              <div className="border border-dashed border-slate-300 rounded-xl p-3 mb-2 bg-slate-50/60">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <p className="text-sm font-medium text-slate-800">
                      Matn orqali tez mavzu qo‘shish
                    </p>
                    <p className="text-xs text-slate-500">
                      Har bir satr alohida mavzu hisoblanadi. Masalan:
                      <br />
                      <span className="font-mono">
                        1-mavzu. Chizmachilik faniga kirish
                      </span>
                      <br />
                      <span className="font-mono">
                        2-mavzu. Chizmalar turlari
                      </span>
                    </p>
                  </div>
                </div>
                <textarea
                  rows={4}
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white resize-y"
                  placeholder={
                    "1-mavzu. Chizmachilik faniga kirish\n2-mavzu. Chizmalar turlari\n3-mavzu. Masshtablar..."
                  }
                />
                <div className="flex justify-end gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setBulkOpen(false)}
                    className="px-3 py-1.5 text-xs rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100"
                  >
                    Bekor qilish
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkAdd}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-xl bg-blue-600 text-white hover:bg-blue-700"
                  >
                    <FiPlus className="w-4 h-4" />
                    Mavzularni qo‘shish
                  </button>
                </div>
              </div>
            )}

            {/* Yangi mavzu forma */}
            <div className="border border-slate-100 bg-slate-50 rounded-xl p-3 md:p-4 space-y-3">
              <p className="text-xs font-medium text-slate-700 uppercase tracking-wide">
                Yangi mavzu qo‘shish
              </p>
              <div className="grid md:grid-cols-5 gap-3">
                <input
                  type="text"
                  value={newTopic.section}
                  onChange={(e) =>
                    setNewTopic((t) => ({ ...t, section: e.target.value }))
                  }
                  className="md:col-span-2 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  placeholder="Bo‘lim nomi (ixtiyoriy) masalan: I bob. Kirish"
                />
                <input
                  type="text"
                  value={newTopic.title}
                  onChange={(e) =>
                    setNewTopic((t) => ({ ...t, title: e.target.value }))
                  }
                  className="md:col-span-2 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  placeholder="Mavzu nomi *"
                />
                <input
                  type="text"
                  value={newTopic.from}
                  onChange={(e) =>
                    setNewTopic((t) => ({ ...t, from: e.target.value }))
                  }
                  className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  placeholder="Boshlanish sahifa (12)"
                />
                <input
                  type="text"
                  value={newTopic.to}
                  onChange={(e) =>
                    setNewTopic((t) => ({ ...t, to: e.target.value }))
                  }
                  className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  placeholder="Oxirgi sahifa (15)"
                />
              </div>

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <textarea
                  rows={2}
                  value={newTopic.note}
                  onChange={(e) =>
                    setNewTopic((t) => ({ ...t, note: e.target.value }))
                  }
                  className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white resize-y"
                  placeholder="Izoh (ixtiyoriy) masalan: Ushbu mavzuda proeksiyalar tushunchasi beriladi."
                />
                <button
                  type="button"
                  onClick={addTopic}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  <FiPlus className="w-4 h-4" />
                  Mavzuni qo‘shish
                </button>
              </div>
            </div>

            {/* Mavzular ro‘yxati */}
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="hidden md:grid grid-cols-12 bg-slate-100 text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
                <div className="col-span-1 px-3 py-2 border-r border-slate-200">
                  #
                </div>
                <div className="col-span-3 px-3 py-2 border-r border-slate-200">
                  Bo‘lim
                </div>
                <div className="col-span-4 px-3 py-2 border-r border-slate-200">
                  Mavzu nomi
                </div>
                <div className="col-span-2 px-3 py-2 border-r border-slate-200">
                  Sahifalar
                </div>
                <div className="col-span-2 px-3 py-2 text-right">
                  Amallar
                </div>
              </div>

              {topics.length === 0 && (
                <div className="px-4 py-6 text-sm text-slate-500 text-center bg-white">
                  Hozircha mavzular qo‘shilmagan. Yuqoridagi forma yoki
                  &laquo;Bulk qo‘shish&raquo; orqali mavzu qo‘shing.
                </div>
              )}

              {topics.map((t) => (
                <div
                  key={t.id}
                  className="grid grid-cols-1 md:grid-cols-12 border-t border-slate-200 bg-white hover:bg-slate-50"
                >
                  <div className="md:col-span-1 px-3 py-2 text-xs md:text-sm font-medium text-slate-700 flex items-center justify-between md:justify-start">
                    <span>{t.order}</span>
                    <span className="md:hidden text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                      Mavzu
                    </span>
                  </div>

                  <div className="md:col-span-3 px-3 py-2 text-xs md:text-sm text-slate-700">
                    {t.section || (
                      <span className="text-slate-400 italic">
                        Bo‘lim ko‘rsatilmagan
                      </span>
                    )}
                  </div>

                  <div className="md:col-span-4 px-3 py-2 text-xs md:text-sm text-slate-900">
                    {t.title}
                    {t.note && (
                      <p className="text-[11px] text-slate-500 mt-1">
                        {t.note}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2 px-3 py-2 text-xs md:text-sm text-slate-700">
                    {t.from || t.to ? (
                      <>
                        {t.from && `s.${t.from}`}{" "}
                        {t.to && `– s.${t.to}`}
                      </>
                    ) : (
                      <span className="text-slate-400 italic">
                        Ko‘rsatilmagan
                      </span>
                    )}
                  </div>

                  <div className="md:col-span-2 px-3 py-2 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => deleteTopic(t.id)}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-red-100 text-red-500 hover:bg-red-50"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Block 4: Submit */}
          <section className="flex flex-col md:flex-row items-center justify-between gap-3 mt-2">
            <div className="text-xs text-slate-500">
              Avval darslik ma’lumotlarini to‘ldiring, so‘ng mavzularni
              tekshirib chiqing. Keyin saqlash tugmasini bosing.
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-sm rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-100"
              >
                Tozalash
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <FiSave className="w-4 h-4" />
                {isLoading ? "Saqlanmoqda..." : "Darslikni saqlash"}
              </button>
            </div>
          </section>
        </form>
      </div>
    </div>
  );
}
